/**
 * "Return to consultation" — the way back into a call the doctor navigated away
 * from.
 *
 * Leaving the call screen never ends the call, so without this the consultation
 * is still running with the patient waiting in it and nothing on screen saying
 * so. The bar is deliberately loud: a live call the doctor has lost track of is
 * worse than a bit of visual noise.
 *
 * It hides itself on the call screen it points at, so it is never a button that
 * navigates you to where you already are.
 */

import { PhoneCall, X } from "lucide-react";
import { Link, useLocation } from "react-router";
import { useSyncExternalStore } from "react";

import { Button } from "@/components/ui/button";
import { useNow } from "@/hooks/common/use-now";
import {
  clearActiveCall,
  getActiveCall,
  getActiveCallServerSnapshot,
  subscribeActiveCall,
} from "@/services/video-call/active-call-store";

export function useActiveCall() {
  return useSyncExternalStore(subscribeActiveCall, getActiveCall, getActiveCallServerSnapshot);
}

function elapsed(startedAt: string, now: Date): string {
  const seconds = Math.max(0, Math.round((now.getTime() - new Date(startedAt).getTime()) / 1000));
  const minutes = Math.floor(seconds / 60);
  if (minutes < 1) return "just now";
  if (minutes < 60) return `${minutes} min`;
  return `${Math.floor(minutes / 60)}h ${minutes % 60}m`;
}

export function ActiveCallBar() {
  const active = useActiveCall();
  const { pathname } = useLocation();
  // The shared 30-second tick already driving the bookings lists.
  const now = useNow();

  if (!active) return null;
  const callPath = `/bookings/${active.bookingId}/call`;
  if (pathname === callPath) return null;

  return (
    <div
      role="status"
      className="flex flex-wrap items-center gap-3 border-b border-success/25 bg-success/10 px-4 py-2 sm:px-6 lg:px-8"
    >
      <span className="relative flex size-2.5 shrink-0" aria-hidden>
        <span className="absolute inline-flex size-full animate-ping rounded-full bg-success opacity-60" />
        <span className="relative inline-flex size-2.5 rounded-full bg-success" />
      </span>

      <p className="min-w-0 flex-1 text-xs leading-relaxed sm:text-sm">
        <span className="font-semibold">Consultation in progress</span>
        <span className="text-muted-foreground">
          {" — "}
          {active.patientName} · {elapsed(active.startedAt, now)}
        </span>
      </p>

      <Button asChild size="sm" className="h-7 gap-1.5 px-2.5 text-xs">
        <Link to={callPath}>
          <PhoneCall className="size-3.5" aria-hidden />
          Return to consultation
        </Link>
      </Button>

      {/*
        Dismiss hides the reminder; it does not end the call. Labelled to say so,
        because a doctor pressing X expecting to hang up and leaving the patient
        in the room would be the worst possible reading of this control.
      */}
      <Button
        type="button"
        variant="ghost"
        size="icon"
        className="size-7 shrink-0"
        onClick={() => clearActiveCall(active.bookingId)}
        aria-label="Hide this reminder (the call stays connected)"
        title="Hide this reminder — the call stays connected"
      >
        <X className="size-3.5" aria-hidden />
      </Button>
    </div>
  );
}
